import { lifecycleEventNames } from '@tunasong/schemas'
import type { Condition, EntityType, Rule } from '@tunasong/schemas'

/** Events this builder offers. Focus events belong to the legacy editor on the same screen. */
export const builderEventNames = lifecycleEventNames

export interface ActionArgSpec {
  /** Key on the rule action's `args`. */
  name: string
  /** Which picker the host must render for it. */
  picker: 'entity' | 'agent'
  /** Shown on the card when the argument has not been chosen. */
  missingLabel: string
}

export interface ActionSpec {
  commandId: string
  label: string
  arg?: ActionArgSpec
  isReversible: boolean
}

/**
 * The actions a user may author.
 *
 * `unlink-agent` is deliberately absent: it exists only as the inverse that
 * `link-agent` records so undo can resolve it, never as something to compose.
 */
export const actionCatalogue: ActionSpec[] = [
  { commandId: 'convert-entity-to-song', label: 'Convert to Song', isReversible: false },
  {
    commandId: 'move-entity',
    label: 'Move to…',
    isReversible: true,
    arg: { name: 'parentId', picker: 'entity', missingLabel: 'Pick a destination first.' },
  },
  {
    commandId: 'link-agent',
    label: 'Attach agent…',
    isReversible: true,
    arg: { name: 'definitionId', picker: 'agent', missingLabel: 'Pick an agent first.' },
  },
]

export const findAction = (commandId: string): ActionSpec | undefined =>
  actionCatalogue.find(spec => spec.commandId === commandId)

/**
 * Kinds a rule may hold only one of. The engine ANDs every condition, so two
 * values of a single-valued field can never both hold — such a rule matches
 * nothing. Keeping these singular is what makes that unbuildable.
 */
const singularConditionKinds: Condition['kind'][] = ['type', 'space']

export const isSingularKind = (kind: Condition['kind']): boolean => singularConditionKinds.includes(kind)

const allConditionKinds: Condition['kind'][] = ['type', 'tag', 'space']

/** Condition kinds still addable to a rule — a used singular kind drops out. */
export const availableConditionKinds = (rule: Rule): Condition['kind'][] =>
  allConditionKinds.filter(kind => !isSingularKind(kind) || !rule.conditions.some(condition => condition.kind === kind))

export interface EntityTypeOption {
  value: EntityType
  label: string
}

/**
 * Entity types offered as the subject of an automation ("When a ‹type› is
 * ‹event›…"). `entityTypeNames` (`@tunasong/schemas`) carries ~45 raw
 * internal names, and letting a rule target any of them makes the exact
 * "impossible rule" the owner ruled out buildable — e.g. "When a profile is
 * created, Convert to Song" (`profile` is the rule's own host entity, never
 * a sensible subject). This list is a deliberate, reviewed curation, not the
 * full set. Excluded, by category:
 *
 * - Editor sub-blocks / inline elements, not standalone filed content:
 *   `section`, `link`.
 * - Response / child records, not something a user files directly:
 *   `pollresponse`, `trackerdata`, `gameresult`, `gameboard`, `comment`,
 *   `audiocomment`.
 * - System / account / graph-plumbing entities, never user content:
 *   `profile` (the rule's own host), `usersettings`, `entitylink`, `ref`,
 *   `searchfolder`, `rule-firing-batch`, `agent` (an AI agent definition).
 * - Table internals, never standalone: `tr`, `th`, `td`, `thead`, `tbody`.
 * - Explicitly disallowed-create placeholders: `facade`.
 * - Import artifacts: `evernote`.
 * - Unfinished plugins: `drawing`.
 * - Dead / unregistered types with no owning plugin: `event`, `sketch`,
 *   `game`, `tracker`, `audiotrack`.
 *
 * The per-instrument types (`amp`, `bass`, `drums`, `guitar`, `mic`, `piano`,
 * `synth`) ARE included. They are not superseded by `gear`: each has its own
 * plugin declaring its own entity type, and `category: 'gear'` is a shared
 * grouping label for the create menu, not a supersession. `gear` is a sibling
 * generic type, not a replacement — a user files a guitar as a `guitar`.
 *
 * Grow this list only for a type a user genuinely files and organizes.
 */
export const automationEntityTypeOptions: EntityTypeOption[] = (
  [
    { value: 'amp', label: 'Amp' },
    { value: 'audio', label: 'Audio' },
    { value: 'audioclip', label: 'Audio Clip' },
    { value: 'bandspace', label: 'Band Space' },
    { value: 'bass', label: 'Bass' },
    { value: 'calendar', label: 'Calendar' },
    { value: 'calendarevent', label: 'Calendar Event' },
    { value: 'chat', label: 'Chat' },
    { value: 'comment', label: 'Comment' },
    { value: 'drums', label: 'Drums' },
    { value: 'effect', label: 'Effect' },
    { value: 'folder', label: 'Folder' },
    { value: 'gear', label: 'Gear' },
    { value: 'guitar', label: 'Guitar' },
    { value: 'image', label: 'Image' },
    { value: 'mic', label: 'Microphone' },
    { value: 'midi', label: 'MIDI' },
    { value: 'musicreview', label: 'Music Review' },
    { value: 'musicxml', label: 'MusicXML' },
    { value: 'note', label: 'Note' },
    { value: 'page', label: 'Page' },
    { value: 'pdf', label: 'PDF' },
    { value: 'piano', label: 'Piano' },
    { value: 'playlist', label: 'Playlist' },
    { value: 'poll', label: 'Poll' },
    { value: 'rider', label: 'Rider' },
    { value: 'score', label: 'Score' },
    { value: 'setlist', label: 'Setlist' },
    { value: 'song', label: 'Song' },
    { value: 'tab', label: 'Tab' },
    { value: 'synth', label: 'Synth' },
    { value: 'table', label: 'Table' },
    { value: 'todo', label: 'Todo' },
    { value: 'video', label: 'Video' },
  ] satisfies EntityTypeOption[]
).sort((a, b) => a.label.localeCompare(b.label))

/** Whether a stored entity type is one this builder still offers. A type
 * condition holding a value outside this list — e.g. a rule saved before
 * this list was curated — has to be rendered honestly rather than falling
 * back to whatever option happens to be first (the same bug class as an
 * unrecognised action command). */
export const isKnownEntityType = (entityType: string): boolean =>
  automationEntityTypeOptions.some(option => option.value === entityType)
